<template>
  <div class="car_list_item" :class="{ compact }" @click="search">
    <div class="image_box">
      <img :src="firstPhoto" @error="imageLoadOnError" />
    </div>
    <div class="info_box">
      <h2>{{ car_data["title"] }}</h2>
      <div class="badges">
        <span class="badge badge_year" v-if="car_data['year']">{{ car_data["year"] }}</span>
        <span class="badge" v-if="car_data['engine_volume']">
          {{ Number(car_data["engine_volume"]).toLocaleString("pl-PL") }} cm³
        </span>
        <span class="badge" v-if="car_data['gear_type']">{{ gearLabel }}</span>
        <span class="badge" v-if="car_data['steering_wheel']">
          {{ car_data["steering_wheel"].toUpperCase() }}
        </span>
        <span
          class="badge"
          :class="repaired ? 'badge_warn' : 'badge_rating'"
          v-if="car_data['auction_rating']"
        >Ocena {{ car_data["auction_rating"] }}</span>
      </div>
      <div class="price_row">
        <span class="price" v-if="price">{{ price }}</span>
        <span class="price price_missing" v-else>Cena na zapytanie</span>
      </div>
      <div class="details">
        <div class="detail_item">
          <span class="detail_label">Przebieg</span>
          <span class="detail_value">{{ formatKm(car_data["mileage"]) }}</span>
        </div>
        <div class="detail_item">
          <span class="detail_label">Dom aukcyjny</span>
          <span class="detail_value">{{ orDash(car_data["auction_location"]) }}</span>
        </div>
        <div class="detail_item">
          <span class="detail_label">Data aukcji</span>
          <span class="detail_value">{{ auctionDate }}</span>
        </div>
        <div class="detail_item">
          <span class="detail_label">Numer Lot</span>
          <span class="detail_value">{{ orDash(car_data["lot"]) }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// Aukcje japońskie — dane z /japan/get-cars w formacie JDM 1:1.
// Katalog amerykański (Copart / IAAI) ma własny komponent: CarListItem.vue.
import { goToResults } from "@/utils/embed";
import {
  PLACEHOLDER,
  orDash,
  gearLabel,
  isRepaired,
  formatPrice,
  formatKm,
  formatAuctionDate,
  galleryPhotos,
} from "@/utils/jdm";

export default {
  name: "JdmCarListItem",
  props: ["car_data", "compact"],
  computed: {
    firstPhoto() {
      return galleryPhotos(this.car_data)[0];
    },
    gearLabel() {
      return gearLabel(this.car_data?.gear_type);
    },
    repaired() {
      return isRepaired(this.car_data?.auction_rating);
    },
    price() {
      return formatPrice(this.car_data?.start_price);
    },
    auctionDate() {
      return formatAuctionDate(this.car_data?.auction_date);
    },
  },
  methods: {
    // Wystawione dla szablonu — Options API nie widzi importów bezpośrednio.
    orDash,
    formatKm,
    imageLoadOnError(e) {
      e.target.src = PLACEHOLDER;
    },
    search() {
      // Otwieramy aukcję, zachowując aktywne filtry, aby powrót do wyników je
      // odtworzył. Parametr `lot_number` w URL zostaje dla zgodności z
      // WordPressem, ale niesie `post_id` — to on identyfikuje aukcję.
      // W osadzeniu w iframe nawigujemy stroną nadrzędną, natywnie — routerem.
      goToResults(this.$router, {
        ...this.$route.query,
        lot_number: this.car_data["post_id"],
      });
    },
  },
};
</script>

<style scoped>
.car_list_item {
  background: #111;
  border: 1px solid rgba(255, 255, 255, 0.07);
  cursor: pointer;
  display: flex;
  gap: 0;
  align-items: stretch;
  width: 100%;
  box-sizing: border-box;
  transition: border-color 0.18s, background 0.18s;
  font-family: 'DM Sans', sans-serif;
}

.car_list_item:hover {
  background: #161616;
  border-color: rgba(255, 255, 255, 0.14);
}

.image_box {
  flex: 0 0 260px;
  width: 260px;
  aspect-ratio: 4/3;
  background: #1a1a1a;
  overflow: hidden;
  flex-shrink: 0;
}

.image_box img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.car_list_item:hover .image_box img {
  transform: scale(1.03);
}

.info_box {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 0;
  min-width: 0;
  padding: 22px 24px;
}

h2 {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.2;
}

/* Spec badges */
.badges {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.badge {
  padding: 5px 11px;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.65);
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.08);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.badge_year {
  background: rgba(210, 31, 31, 0.15);
  border-color: rgba(210, 31, 31, 0.3);
  color: #d21f1f;
}

.badge_rating {
  background: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.85);
}

/* Naprawa powypadkowa (ocena R / R2) */
.badge_warn {
  background: rgba(122, 74, 0, 0.55);
  border-color: rgba(255, 207, 122, 0.35);
  color: #ffcf7a;
}

/* Cena wywoławcza */
.price_row {
  display: flex;
  align-items: baseline;
  gap: 8px;
}

.price {
  font-family: 'Space Grotesk', sans-serif;
  font-size: 20px;
  font-weight: 700;
  color: #fff;
  line-height: 1.1;
}

.price_missing {
  font-size: 15px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
}

/* Detail rows */
.details {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 32px;
  margin-top: auto;
}

.detail_item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.detail_label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.32);
}

.detail_value {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  line-height: 1.3;
}

.mono {
  font-family: ui-monospace, 'Cascadia Code', monospace;
  font-size: 12px;
  letter-spacing: 0.03em;
}

/* Compact (grid / 3-column) layout: vertical card, image on top */
.car_list_item.compact {
  flex-direction: column;
}

.car_list_item.compact .image_box {
  width: 100%;
  flex: none;
  aspect-ratio: 16/9;
}

.car_list_item.compact .info_box {
  padding: 18px 20px;
  gap: 10px;
}

.car_list_item.compact h2 {
  font-size: 17px;
}

.car_list_item.compact .price {
  font-size: 17px;
}

.car_list_item.compact .details {
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;
}

@media (max-width: 640px) {
  .car_list_item {
    flex-direction: column;
  }

  .image_box {
    width: 100%;
    flex: none;
    aspect-ratio: 16/9;
  }

  .details {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}
</style>
